import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  IconSparkles,
  IconMessageCircle,
  IconChartDots3,
  IconSearch,
  IconCircleCheckFilled,
  IconChevronDown,
  IconChevronUp,
} from "@tabler/icons-react";
import { cn } from "../../lib/utils";
import type { GptMode } from "../../domains/gpt/stores/modeStore";

type ModePanelProps = {
  selected: GptMode;
  onSelectChange: (mode: GptMode) => void;
  className?: string;
};

// The modes surfaced in the picker, in display order. `beta` drives the
// display-only BETA pill on Evidence (docs/gpt-mode-router.md — Evidence
// is UNGATED, shown to everyone with the tag, not behind a feature flag).
const MODES = [
  { mode: "auto", beta: false },
  { mode: "general", beta: false },
  { mode: "evidence", beta: true },
  { mode: "find_publication", beta: false },
] as const satisfies ReadonlyArray<{ mode: GptMode; beta: boolean }>;

// The short label rendered in the collapsed trigger pill. Kept exhaustive over
// the union so a new mode fails the build until it is given a label.
function modeShortLabel(mode: GptMode, t: (key: string) => string): string {
  switch (mode) {
    case "auto":
      return t("Auto");
    case "general":
      return t("General");
    case "evidence":
      return t("Evidence");
    case "find_publication":
      return t("Search");
    default:
      throw new Error(mode satisfies never);
  }
}

function ModeIcon({ mode }: { mode: GptMode }): React.JSX.Element {
  switch (mode) {
    case "auto":
      return <IconSparkles size={20} stroke={1.5} />;
    case "general":
      return <IconMessageCircle size={20} stroke={1.5} />;
    case "evidence":
      return <IconChartDots3 size={20} stroke={1.5} />;
    case "find_publication":
      return <IconSearch size={20} stroke={1.5} />;
    default:
      throw new Error(mode satisfies never);
  }
}

function modeDescription(mode: GptMode, t: (key: string) => string): string {
  switch (mode) {
    case "auto":
      return t("Juisci picks the best engine for your question.");
    case "general":
      return t("Conversational answers grounded in Juisci and PubMed.");
    case "evidence":
      return t(
        "A citation-anchored synthesis of the evidence, scored for confidence."
      );
    case "find_publication":
      return t("Find publications and get ways to refine your search.");
    default:
      throw new Error(mode satisfies never);
  }
}

/**
 * Mode-selection panel for the chat composer (docs/gpt-mode-router.md) — the
 * expanded half of the mode control (`ModeTrigger` is the collapsed half).
 * Single-select (one engine per turn), unlike the old two-toggle Sources panel.
 */
function ModePanel({
  selected,
  onSelectChange,
  className,
}: ModePanelProps): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <div
      className={cn(
        "flex w-full flex-col gap-0.5 rounded-xl p-2",
        "border border-solid border-ds-slate-200 bg-ds-white",
        "shadow-[0px_4px_12px_rgba(17,24,39,0.08)]",
        className
      )}
    >
      {MODES.map(({ mode, beta }) => (
        <ModeSection
          key={mode}
          icon={<ModeIcon mode={mode} />}
          title={modeShortLabel(mode, t)}
          description={modeDescription(mode, t)}
          beta={beta}
          selected={selected === mode}
          onSelect={() => {
            onSelectChange(mode);
          }}
        />
      ))}
    </div>
  );
}

/** One selectable mode row: icon, title (+ optional BETA pill),
 *  description, and a trailing radio indicator. The whole row is the control. */
function ModeSection({
  icon,
  title,
  description,
  beta,
  selected,
  onSelect,
}: {
  icon: ReactNode;
  title: string;
  description: string;
  beta: boolean;
  selected: boolean;
  onSelect: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={onSelect}
      className={cn(
        "flex w-full items-start gap-3 rounded-xl p-5 text-left",
        "cursor-pointer transition-colors",
        selected ? "bg-ds-mint-100" : "hover:bg-ds-slate-100",
        "focus-visible:outline-none focus-visible:shadow-focus-ring"
      )}
    >
      <span className="flex size-6 shrink-0 items-center justify-center text-ds-moss-800">
        {icon}
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-1.5 font-dm-sans text-ui-body">
        <span className="flex items-center gap-2">
          <span className="font-semibold text-ds-moss-800">{title}</span>
          {beta && (
            <span className="rounded-full bg-ds-mint-100 px-2 py-0.5 font-dm-sans text-ui-note font-medium uppercase tracking-wide text-ds-mint-800">
              {t("Beta")}
            </span>
          )}
        </span>
        <p className="font-normal text-ds-slate-600">{description}</p>
      </div>
      <span className="flex size-6 shrink-0 items-center justify-center">
        {selected ? (
          <IconCircleCheckFilled size={20} className="text-ds-mint-800" />
        ) : (
          <span className="size-4.5 rounded-full border border-ds-slate-200" />
        )}
      </span>
    </button>
  );
}

/**
 * Collapsed half of the mode control: a pill in the composer's button row that
 * names the active mode and expands the panel.
 */
function ModeTrigger({
  selected,
  open,
  onClick,
  controls,
  className,
}: {
  selected: GptMode;
  open: boolean;
  onClick: () => void;
  /** `id` of the panel this trigger expands, for `aria-controls`. */
  controls: string;
  className?: string;
}): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <button
      type="button"
      onClick={onClick}
      aria-expanded={open}
      aria-controls={controls}
      className={cn(
        "flex h-8 shrink-0 cursor-pointer items-center gap-2 rounded-lg py-1 pl-3 pr-2",
        "border border-solid border-ds-slate-200 bg-ds-white",
        "font-dm-sans font-medium text-ui-note text-ds-mint-900",
        "focus-visible:outline-none focus-visible:shadow-focus-ring",
        className
      )}
    >
      {modeShortLabel(selected, t)}
      {open ? (
        <IconChevronDown size={16} stroke={1.25} className="ml-auto" />
      ) : (
        <IconChevronUp size={16} stroke={1.25} className="ml-auto" />
      )}
    </button>
  );
}

export {
  ModePanel,
  ModeTrigger,
  modeDescription,
  modeShortLabel,
  type ModePanelProps,
};
